// ── Shared period type ────────────────────────────────────────────────────────

export interface ScorecardPeriod {
  month: string; // lowercase 3-letter: "jan"–"dec"
  year: number;
}

// ── Final Score API ───────────────────────────────────────────────────────────

export type FinalScorePeriod = ScorecardPeriod;

export interface FinalScoreRequest {
  vendorCode: string;
  period: ScorecardPeriod[];
}

export interface FinalScoreKpiData {
  month: string;
  year: number;
  outOfFiveScore: number;
  outOfHundredScore: number;
  vendorSettingsVersionUsed: number;
  calculatedAt: string; // ISO-8601
}

export interface FinalScoreResponse {
  vendorId: string;
  kpiData: FinalScoreKpiData[];
}

// ─────────────────────────────────────────────────────────────────────────────

// ── KPI Category Score API ────────────────────────────────────────────────────

export interface KpiCategoryScoreRequest {
  vendorCode: string;
  period: ScorecardPeriod[];
}

export interface KpiCategoryScoreData {
  kpiCategoryId: number;
  month: string;
  year: number;
  outOfFiveScore: number;
  totalScore: number;
  vendorSettingsVersionUsed: number;
  calculatedAt: string;
}

export interface KpiCategoryScoreResponse {
  vendorId: string;
  kpiData: KpiCategoryScoreData[];
}

// ── KPI Score API ─────────────────────────────────────────────────────────────

export interface KpiScoreRequest {
  vendorCode: string;
  period: ScorecardPeriod[];
}

export interface KpiScoreData {
  kpiId: number;
  month: string;
  year: number;
  meanValue: number;
  medianValue: number;
  standardDeviation: number;
  outOfFiveScore: number;
  weightedScore: number;
  globalSettingsVersionUsed: number;
  vendorSettingsVersionUsed: number;
  summaryStatisticsCalculatedAt: string;
  scoresCalculatedAt: string;
}

export interface KpiScoreResponse {
  vendorId: string;
  kpiData: KpiScoreData[];
}

// ── KPI Weekly Score API ──────────────────────────────────────────────────────

export interface WeeklyScorecardPeriod {
  week: number;
  year: number;
}

export interface KpiScoreWeeklyRequest {
  vendorCode: string;
  period: WeeklyScorecardPeriod[];
}

export interface KpiScoreWeeklyData {
  kpiId: number;
  week: number;
  year: number;
  meanValue: number | null;
  medianValue: number | null;
  standardDeviation: number | null;
  outOfFiveScore: number | null;
  weightedScore: number | null;
  summaryStatisticsCalculatedAt: string | null;
  scoresCalculatedAt: string | null;
}

export interface KpiScoreWeeklyResponse {
  vendorId: string;
  kpiData: KpiScoreWeeklyData[];
}

// ─────────────────────────────────────────────────────────────────────────────

export enum KPIFrequency {
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export interface TrendsDataPoint {
  month: string;
  trendsOutOfFiveDataThisYear: number | null;
  trendsOutOfFiveDataPreviousYear: number | null;
  trendsWeightedDataThisYear: number | null;
  trendsWeightedDataPreviousYear: number | null;
  trendsMeanValuesThisYear: number | null;
  trendsMeanValuesPreviousYear: number | null;
}

export interface KPITrendComponentInput {
  kpiId: number;
  kpiWeight: number; //card is skipped when weight = 0
  kpiName: string;
  kpiDescription: string;
  kpiUnit: string;
  kpiMaxOutofFiveScore?: number;
  kpiMaxWeightedScore?: number;
  trends: TrendsDataPoint[];
  footerText: string;
  expanded: boolean;
  isLowerBetter: boolean;
  isShopView?: boolean; // defaults to true; false = use weighted scores instead of out-of-five
  frequency: KPIFrequency;
}

export interface CategoryCardInput {
  categoryId: number; // 1=Lead Time 2=Process Compliance 3=eEIR 4=Audit Result
  categoryWeight: number; // VendorSettings kpiCategories[].weight — card is skipped when 0
  categoryName: string;
  outOfFiveScore: number;
  maxOutOfFiveScore?: number; // default 5
  outOfTotalScore: number;
  maxOutOfTotalScore: number;
  isShopView?: boolean; // default false → show weighted score
  kpis: KPITrendComponentInput[];
  expanded?: boolean;
}
